/* ============================================================
   NAHLA — MOTION FRAMEWORK
   Scroll-triggered reveals + parallax for the storefront.
   Works in all modern browsers. Respects prefers-reduced-motion.
   ============================================================ */

(function () {
  'use strict';

  const REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // ---------- 0. Loading screen ----------
  //
  // Shown on first visit per session. Fades out when window 'load' fires
  // OR after a max timeout so it never hangs. Uses sessionStorage so
  // returning navigations within the same session skip the loader.

  function setupLoader() {
    const loader = document.querySelector('[data-nahla-loader]');
    if (!loader) return;

    // If we already showed the loader this session, hide instantly
    let alreadyShown = false;
    try {
      alreadyShown = sessionStorage.getItem('nahla-loader-shown') === '1';
    } catch (e) { /* privacy mode */ }

    if (alreadyShown || REDUCED) {
      loader.classList.add('is-instant');
      return;
    }

    function dismiss() {
      loader.classList.add('is-hidden');
      try { sessionStorage.setItem('nahla-loader-shown', '1'); } catch (e) {}
      // Remove from DOM after transition finishes so it can't intercept clicks
      setTimeout(() => loader.remove(), 1200);
    }

    // Dismiss on window.load OR after a safety timeout of 2.5s
    let dismissed = false;
    function safeDismiss() {
      if (dismissed) return;
      dismissed = true;
      // Small delay so the wordmark breathes for at least one cycle
      setTimeout(dismiss, 600);
    }

    if (document.readyState === 'complete') {
      safeDismiss();
    } else {
      window.addEventListener('load', safeDismiss);
    }
    setTimeout(safeDismiss, 2500);
  }

  setupLoader();

  // ---------- 1. Reveal-on-scroll system ----------
  //
  // Any element with .nahla-reveal or .nahla-stagger gets .is-visible
  // added when it crosses 15% into the viewport. CSS handles the visual.
  // Elements only animate once (no repeat on scroll back up).

  function setupReveals() {
    if (REDUCED) return;

    const targets = document.querySelectorAll('.nahla-reveal:not(.is-visible), .nahla-stagger:not(.is-visible)');
    if (!targets.length) return;

    const io = new IntersectionObserver((entries, observer) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        }
      });
    }, {
      threshold: 0.15,
      rootMargin: '0px 0px -8% 0px'
    });

    targets.forEach(el => io.observe(el));
  }

  // ---------- 2. Auto-tag common section content ----------
  //
  // We can't always edit Liquid templates to add .nahla-reveal manually.
  // This tags headings + key content blocks inside every Shopify section
  // so they get the reveal treatment automatically.

  function autoTagSections() {
    if (REDUCED) return;

    const sections = document.querySelectorAll('.shopify-section');
    sections.forEach(section => {
      // skip header/footer
      if (section.closest('#header-group, #footer-group')) return;
      // skip hero — it's already in view on load; we don't want a fade-in there
      if (section.querySelector('.hero')) return;
      // skip if already tagged
      if (section.dataset.nahlaTagged) return;

      // Tag headings and direct text blocks
      const headings = section.querySelectorAll('h1, h2, h3, h4');
      headings.forEach(h => h.classList.add('nahla-reveal'));

      // Tag images inside media-with-content (whole block, so image + text reveal together)
      const mwc = section.querySelector('.media-with-content');
      if (mwc) {
        mwc.classList.add('nahla-reveal');
      }

      section.dataset.nahlaTagged = '1';
    });
  }

  // ---------- 3. Scroll-driven parallax for hero + media images ----------

  function collectParallax() {
    return {
      hero:  Array.from(document.querySelectorAll('.hero img, .hero__image, .hero__media-wrapper img')),
      media: Array.from(document.querySelectorAll('.media-with-content img, .nahla-parallax'))
    };
  }

  let parallax = collectParallax();

  function updateParallax() {
    if (REDUCED) return;
    const vh = window.innerHeight || document.documentElement.clientHeight;

    parallax.hero.forEach(img => {
      const r = img.getBoundingClientRect();
      if (r.bottom < -100 || r.top > vh + 100) return;
      const progress = Math.max(0, Math.min(1, 1 - (r.bottom / (vh + r.height))));
      const scale = 1 + progress * 0.22;          // was 0.12 — bolder zoom
      const ty = progress * -8;                   // subtle upward drift (parallax)
      img.style.transform = `scale(${scale.toFixed(4)}) translateY(${ty.toFixed(2)}%)`;
    });

    parallax.media.forEach(img => {
      const r = img.getBoundingClientRect();
      if (r.bottom < -100 || r.top > vh + 100) return;
      const ec = r.top + r.height / 2;
      const vc = vh / 2;
      const dist = Math.abs(ec - vc) / (vh / 2 + r.height / 2);
      const proximity = 1 - Math.min(1, dist);
      const scale = 0.90 + proximity * 0.18;      // was 0.96 → 1.04, now 0.90 → 1.08 (wider, more dramatic)
      img.style.transform = `scale(${scale.toFixed(4)})`;
    });
  }

  let ticking = false;
  function onScroll() {
    if (!ticking) {
      requestAnimationFrame(() => {
        updateParallax();
        ticking = false;
      });
      ticking = true;
    }
  }

  // ---------- 4. Init ----------

  function init() {
    autoTagSections();
    setupReveals();
    parallax = collectParallax();
    updateParallax();
  }

  // First run
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-run when sections are re-rendered (theme editor, soft reloads)
  document.addEventListener('shopify:section:load', init);
  document.addEventListener('shopify:section:select', init);

  // Scroll + resize handlers (passive for perf)
  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', onScroll, { passive: true });
  window.addEventListener('load', init);
})();
